import { Action } from '@ngrx/store';
import _ from 'lodash';
import { GameDayModel } from '../../models/gameday-model';
import { GameDayViewType } from '../../state/data/state';
import {
  segmentToTranche,
  Tranche
} from '../../gameday/data/gameDay-data.transformations';
import {
  OrdersSegment,
  SegmentGroupExtractor,
  segmentOrders
} from '../../gameday/data/gameDay-data.segmentations';
import { GameDayCalculatedData } from '../../gameday/data/gameDay-data.calculation';
import {
  getGroupExtractor,
  MANAGER_ROLE_SENIOR_MANAGER,
  maturityIdSegmentExtractor,
  trancheExtractors
} from '../../gameday/mappers/gameDay.extractors';
import {
  SetFilterByCompanyType,
  SetFilterByMemberPriority,
  SetFilterByRetailPriority
} from '../../state/monitor/actions';
import { LabelGenerator, ItemGroup } from '../models/label';
import {
  MonitorViewType,
  MonitorFilter,
  MonitorOverviewItem
} from '../models/monitor';
import { CompanyType } from '../models/company';
import { RetailPriority, MemberPriority } from '../models/priority';
import { MonitorState } from '../../state/monitor/state';
import { IRowNode } from 'ag-grid-community';

export const VIEW_TYPES_BY_TAB_INDEX_MAPPING: {
  [key: number]: MonitorViewType;
} = {
  0: MonitorViewType.Overview,
  1: MonitorViewType.Retail,
  2: MonitorViewType.Priority
};

export const MONITOR_TO_GAME_DAY_VIEW_TYPE_MAPPING: {
  [key: string]: GameDayViewType;
} = {
  [MonitorViewType.Overview]: GameDayViewType.PriorityType,
  [MonitorViewType.Retail]: GameDayViewType.RetailType,
  [MonitorViewType.Priority]: GameDayViewType.InstitutionalType
};

function getViewTypeTabIndexes(): {
  [key: number]: number;
} {
  return _(VIEW_TYPES_BY_TAB_INDEX_MAPPING)
    .invert()
    .mapValues(v => +v)
    .value();
}

export const COMPANY_TYPE_FILTER_GROUP_ID = 'companyType';
const COMPANY_TYPE_FILTER: ItemGroup = {
  id: COMPANY_TYPE_FILTER_GROUP_ID,
  items: [
    {
      id: '',
      caption: 'All'
    },
    {
      id: CompanyType[CompanyType.Lead],
      caption: 'Lead'
    },
    {
      id: CompanyType[CompanyType.Syndicate],
      caption: 'Syndicate'
    }
  ]
};

export const MEMBER_PRIORITY_FILTER_GROUP_ID = 'memberPriority';
const MEMBER_PRIORITY_FILTER: ItemGroup = {
  id: MEMBER_PRIORITY_FILTER_GROUP_ID,
  items: [
    {
      id: '',
      caption: 'All'
    },
    {
      id: MemberPriority[MemberPriority.Member],
      caption: 'Member'
    },
    {
      id: MemberPriority[MemberPriority.Priority],
      caption: 'Priority'
    }
  ]
};

export const RETAIL_TYPE_FILTER_GROUP_ID = 'retailPriority';
export const RETAIL_TYPE_FILTER: ItemGroup = {
  id: RETAIL_TYPE_FILTER_GROUP_ID,
  items: [
    {
      id: '',
      caption: 'All'
    },
    {
      id: RetailPriority[RetailPriority.Retail],
      caption: 'Priority Retail'
    },
    {
      id: RetailPriority[RetailPriority.NonRetail],
      caption: 'Priority Non-Retail'
    }
  ]
};

export const FILTER_ITEM_GROUPS_OVERVIEW: ItemGroup[] = [COMPANY_TYPE_FILTER];
export const FILTER_ITEM_GROUPS_RETAIL: ItemGroup[] = [
  COMPANY_TYPE_FILTER,
  MEMBER_PRIORITY_FILTER
];
export const FILTER_ITEM_GROUPS_PRIORITY: ItemGroup[] = [
  COMPANY_TYPE_FILTER,
  RETAIL_TYPE_FILTER
];

export function getTabIndexByViewType(
  viewType: MonitorViewType,
  retailDisabled: boolean
): number {
  return (
    getViewTypeTabIndexes()[
      viewType === MonitorViewType.Retail && retailDisabled
        ? MonitorViewType.Overview
        : viewType
    ] || 0
  );
}

export function getFilterItemGroups(viewType: MonitorViewType): ItemGroup[] {
  switch (viewType) {
    case MonitorViewType.Overview:
      return FILTER_ITEM_GROUPS_OVERVIEW;
    case MonitorViewType.Retail:
      return FILTER_ITEM_GROUPS_RETAIL;
    case MonitorViewType.Priority:
      return FILTER_ITEM_GROUPS_PRIORITY;
    default:
      return undefined;
  }
}

export function getFilterAction(groupId: string, value: string): Action {
  switch (groupId) {
    case COMPANY_TYPE_FILTER_GROUP_ID:
      return value
        ? new SetFilterByCompanyType(CompanyType[value])
        : new SetFilterByCompanyType();
    case MEMBER_PRIORITY_FILTER_GROUP_ID:
      return value
        ? new SetFilterByMemberPriority(MemberPriority[value])
        : new SetFilterByMemberPriority();
    case RETAIL_TYPE_FILTER_GROUP_ID:
      return value
        ? new SetFilterByRetailPriority(RetailPriority[value])
        : new SetFilterByRetailPriority();
    default:
      throw new Error(`groupId ${groupId} not implemented`);
  }
}

export function getFilterFormModel(state: MonitorState): MonitorFilter {
  return {
    companyType: CompanyType[state.filterByCompanyType] || '',
    memberPriority: MemberPriority[state.filterByMemberPriority] || '',
    retailPriority: RetailPriority[state.filterByRetailPriority] || ''
  };
}

export function isLead(order: GameDayModel.Order): boolean {
  return order.syndicateDepartment.role === MANAGER_ROLE_SENIOR_MANAGER;
}

export function filterByCompanyType(
  filteredData: GameDayCalculatedData,
  companyType: CompanyType | undefined
): GameDayCalculatedData {
  if (companyType === undefined) {
    return filteredData;
  }

  const orders =
    filteredData.orders &&
    filteredData.orders.filter(order => {
      switch (companyType) {
        case CompanyType.Lead:
          return isLead(order);
        case CompanyType.Syndicate:
          return !isLead(order);
        default:
          return true;
      }
    });

  return { ...filteredData, orders };
}

export function isMember(order: GameDayModel.Order): boolean {
  return order.orderType.code === 'MEM';
}

export function isPriority(order: GameDayModel.Order): boolean {
  return order.orderType.code === 'GN' || order.orderType.code === 'ND';
}

export function filterByMemberPriority(
  filteredData: GameDayCalculatedData,
  memberPriority?: MemberPriority | undefined
): GameDayCalculatedData {
  if (memberPriority === undefined) {
    return filteredData;
  }

  const orders =
    filteredData.orders &&
    filteredData.orders.filter(order => {
      switch (memberPriority) {
        case MemberPriority.Member:
          return isMember(order);
        case MemberPriority.Priority:
          return isPriority(order);
        default:
          return true;
      }
    });
  return { ...filteredData, orders };
}

export function filteredDataToTranche(
  filteredData: GameDayCalculatedData,
  viewType: GameDayViewType,
  maturityId: string
): Tranche | undefined {
  if (
    !maturityId ||
    !(filteredData.deal.maturities.findIndex(m => m.id === maturityId) > -1)
  ) {
    return undefined;
  }

  const groupExtractors: SegmentGroupExtractor<any> = getGroupExtractor(
    filteredData,
    viewType
  );
  const maturity: GameDayModel.Maturity = filteredData.deal.maturities.find(
    m => m.id === maturityId
  );
  const segments: OrdersSegment<any>[] = segmentOrders(
    filteredData.orders,
    [maturity],
    maturityIdSegmentExtractor,
    groupExtractors
  );

  return segmentToTranche(
    segments[0],
    trancheExtractors[0].size,
    trancheExtractors[0].top,
    trancheExtractors[0].bottom
  );
}

export function isPriorityRetail(order: GameDayModel.Order): boolean {
  return order.retailInd;
}

export function filterByRetailPriority(
  filteredData: GameDayCalculatedData,
  retailPriority: RetailPriority | undefined
): GameDayCalculatedData {
  if (retailPriority === undefined) {
    return filteredData;
  }

  const orders = filteredData.orders.filter((order: GameDayModel.Order) => {
    switch (retailPriority) {
      case RetailPriority.Retail:
        return isPriorityRetail(order);
      case RetailPriority.NonRetail:
        return !isPriorityRetail(order);
      default:
        return true;
    }
  });

  return { ...filteredData, orders };
}

export function getCaptions(
  itemGroups: ItemGroup[],
  filter: MonitorFilter
): string[] {
  return itemGroups
    .map(group => group.items.find(item => item.id === filter[group.id]))
    .filter(item => !!item && !!item.id)
    .map(item => item.caption);
}

export const overviewLabelGenerator: LabelGenerator = (
  itemGroups: ItemGroup[],
  filter: MonitorFilter
) => {
  return getCaptions(itemGroups, filter).join(' ');
};

export const RETAIL_LABEL = 'Retail';
export const retailLabelGenerator: LabelGenerator = (
  itemGroups: ItemGroup[],
  filter: MonitorFilter
) => {
  const captions: string[] = getCaptions(itemGroups, filter);
  if (captions.length === 1) {
    captions.push(RETAIL_LABEL);
  }
  return captions.join(' ');
};

export const PRIORITY_LABEL = 'Priority';
export const priorityLabelGenerator: LabelGenerator = (
  itemGroups: ItemGroup[],
  filter: MonitorFilter
) => {
  const label: string = getCaptions(itemGroups, filter).join(' ');
  if (!label.includes(PRIORITY_LABEL)) {
    return `${label} ${PRIORITY_LABEL}`;
  }
  return label;
};

export function maturityComparator(
  _valA,
  _valB,
  nodeA: IRowNode,
  nodeB: IRowNode
): number {
  const a = nodeA.data as MonitorOverviewItem;
  const b = nodeB.data as MonitorOverviewItem;

  if (a.maturity < b.maturity) {
    return -1;
  } else if (a.maturity > b.maturity) {
    return 1;
  } else if (a.series < b.series) {
    return -1;
  } else if (a.series > b.series) {
    return 1;
  } else if (a.coupon < b.coupon) {
    return -1;
  } else if (a.coupon > b.coupon) {
    return 1;
  } else if (a.amount > b.amount) {
    return -1;
  } else if (a.amount < b.amount) {
    return 1;
  }
  return 0;
}
